/**
 * Staff Details & Personality - Constants & Data
 * Centralized configuration for personality assessment
 */

// ===== BASIC OPTIONS =====
export const COMMUNICATION_STYLES = ['Direct', 'Diplomatic', 'Reserved', 'Expressive'];
export const WORK_STYLES = ['Independent', 'Collaborative', 'Flexible', 'Structured'];
export const LEADERSHIP_LEVELS = ['High', 'Medium', 'Low'];
export const CONFLICT_STYLES = ['Confrontational', 'Avoidant', 'Collaborative', 'Accommodating'];
export const STRESS_LEVELS = ['Excellent', 'Good', 'Moderate', 'Needs Support'];
export const MOTIVATION_TYPES = ['Achievement', 'Recognition', 'Growth', 'Stability'];
export const TEAM_ROLES = ['Leader', 'Supporter', 'Innovator', 'Executor', 'Mediator'];
export const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Intern'];
export const EDUCATION_LEVELS = ['SPM', 'STPM', 'Diploma', 'Degree', 'Masters', 'PhD'];
export const GENDERS = ['Male', 'Female'];
export const MARITAL_STATUSES = ['Single', 'Married', 'Divorced', 'Widowed'];
export const RACES = ['Malay', 'Chinese', 'Indian', 'Others'];
export const RELIGIONS = ['Islam', 'Buddhism', 'Hinduism', 'Christianity', 'Others'];
export const STATES = ['Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 'Pahang', 'Perak', 'Perlis', 'Pulau Pinang', 'Sabah', 'Sarawak', 'Selangor', 'Terengganu', 'W.P. Kuala Lumpur', 'W.P. Labuan', 'W.P. Putrajaya'];

export const STRENGTH_OPTIONS = [
  'Analytical Thinking', 'Communication', 'Creativity', 'Decision Making',
  'Emotional Intelligence', 'Leadership', 'Problem Solving', 'Teamwork',
  'Time Management', 'Technical Skills', 'Adaptability', 'Attention to Detail'
];

export const IMPROVEMENT_OPTIONS = [
  'Public Speaking', 'Delegation', 'Conflict Resolution', 'Time Management',
  'Technical Skills', 'Communication', 'Leadership', 'Patience',
  'Attention to Detail', 'Prioritization', 'Networking', 'Decision Making'
];

// ===== VAKD - Cara Otak Terima & Proses Maklumat =====
export const VAKD_TYPES = [
  { value: 'Visual', label: '👁️ Visual', desc: 'Senang faham bila nampak - suka chart, slide, contoh' },
  { value: 'Audio', label: '👂 Audio', desc: 'Senang faham bila dengar - ingat tone & ayat' },
  { value: 'Kinestetik', label: '✋ Kinestetik', desc: 'Faham bila buat sendiri - learning by doing' },
  { value: 'Digital', label: '🧠 Digital', desc: 'Logik, struktur, data - suka step & reason' }
];

// ===== DOPE - Cara Otak Bertindak & Buat Keputusan =====
export const DOPE_TYPES = [
  { value: 'Dove', label: '🕊️ Dove', desc: 'Stabil, loyal, support - elak konflik', role: 'Ops support, HR, admin' },
  { value: 'Owl', label: '🦉 Owl', desc: 'Analitik, detail, perfectionist - slow but accurate', role: 'Finance, QA, data, compliance' },
  { value: 'Peacock', label: '🦚 Peacock', desc: 'Energy, idea, people person - cepat bosan', role: 'Sales, marketing, content' },
  { value: 'Eagle', label: '🦅 Eagle', desc: 'Result-driven, decisive - suka control & challenge', role: 'Leader, closer, project head' }
];

// ===== COLOUR BRAIN - Cara Otak Berfikir & Process =====
export const COLOUR_BRAIN_TYPES = [
  { value: 'Red', label: '🔴 Red Brain', color: '#ef4444', bgColor: 'from-red-500 to-red-600', desc: 'Action-oriented, competitive, decisive, fast-paced', traits: ['Quick decision maker', 'Results-focused', 'Direct communicator', 'Risk taker'], role: 'Sales closer, Leader, Crisis manager' },
  { value: 'Blue', label: '🔵 Blue Brain', color: '#3b82f6', bgColor: 'from-blue-500 to-blue-600', desc: 'Analytical, methodical, detail-oriented, systematic', traits: ['Data-driven', 'Quality-focused', 'Organized', 'Thorough'], role: 'Finance, Analyst, QA, Compliance' },
  { value: 'Green', label: '🟢 Green Brain', color: '#22c55e', bgColor: 'from-green-500 to-green-600', desc: 'Creative, innovative, big-picture, visionary', traits: ['Creative thinker', 'Problem solver', 'Sees possibilities', 'Future-focused'], role: 'Strategy, Innovation, R&D, Marketing' },
  { value: 'Yellow', label: '🟡 Yellow Brain', color: '#eab308', bgColor: 'from-yellow-500 to-yellow-600', desc: 'People-oriented, harmonious, collaborative, empathetic', traits: ['Team builder', 'Great listener', 'Supportive', 'Relationship-focused'], role: 'HR, Customer service, Team lead, Mediator' }
];

// ===== HANDLING GUIDES =====

export const VAKD_HANDLING_GUIDE = {
  'Visual': {
    communication: ['Gunakan slides/diagram dalam meeting', 'Share screenshot/mockup bila explain', 'Bagi written summary dengan bullet points'],
    instruction: ['Tunjuk contoh visual sebelum suruh buat', 'Guna whiteboard untuk explain flow', 'Bagi checklist bergambar'],
    motivation: ['Tunjukkan progress chart', 'Bagi recognition secara visual (certificate, dashboard)', 'Share success stories dengan images'],
    warning: ['Jangan bagi arahan panjang tanpa visual', 'Elak meeting tanpa slides', 'Jangan expect faham dari voice note sahaja'],
    bestTasks: ['Design work', 'Presentation', 'Process mapping', 'Dashboard creation']
  },
  'Audio': {
    communication: ['Brief secara verbal/call', 'Guna voice note untuk update', 'Discuss dalam meeting berbanding email'],
    instruction: ['Explain step-by-step secara lisan', 'Bagi masa untuk tanya soalan', 'Record training session'],
    motivation: ['Puji secara verbal dan public', 'Regular 1-on-1 calls', 'Bagi feedback terus menerus'],
    warning: ['Jangan hantar email panjang tanpa call', 'Elak arahan bertulis sahaja', 'Jangan skip verbal confirmation'],
    bestTasks: ['Customer calls', 'Team coordination', 'Training delivery', 'Podcast/Video']
  },
  'Kinestetik': {
    communication: ['Hands-on demo', 'Walk through process sama-sama', 'Site visit/shadow session'],
    instruction: ['Demo dulu, then biar try', 'Bagi trial & error space', 'Practice session sebelum live'],
    motivation: ['Bagi projek hands-on', 'Allow experimentation', 'Recognize practical achievements'],
    warning: ['Jangan terlalu banyak teori', 'Elak training slides only', 'Jangan expect belajar dari manual'],
    bestTasks: ['Implementation', 'Troubleshooting', 'Setup/Installation', 'Field work']
  },
  'Digital': {
    communication: ['Structured email dengan numbering', 'Share data & statistics', 'Provide documentation links'],
    instruction: ['Bagi SOP dengan step numbers', 'Explain WHY behind setiap step', 'Provide reference materials'],
    motivation: ['Show data-driven results', 'Logical career progression', 'Knowledge-based rewards'],
    warning: ['Jangan bagi arahan vague', 'Elak emotional appeals', 'Jangan skip the reasoning'],
    bestTasks: ['Data analysis', 'Process optimization', 'System design', 'Documentation']
  }
};

export const DOPE_HANDLING_GUIDE = {
  'Dove': {
    leadership: ['Bagi direction yang jelas & stable', 'Elak sudden changes', 'Create safe environment untuk voice out'],
    motivation: ['Appreciation & recognition', 'Job security assurance', 'Team harmony'],
    delegation: ['Bagi task dengan clear timeline', 'Check-in regularly tapi tak pressure', 'Pair dengan supportive teammate'],
    conflict: ['Handle privately, never public', 'Bagi masa untuk process', 'Avoid confrontation'],
    welfare: ['Regular check-ins', 'Stable workload', 'Supportive feedback'],
    warning: ['Jangan push terlalu agresif', 'Elak public criticism', 'Jangan ignore their concerns'],
    strengths: ['Loyal', 'Supportive', 'Good listener', 'Team player'],
    development: ['Assertiveness training', 'Decision making skills', 'Confidence building']
  },
  'Owl': {
    leadership: ['Bagi data lengkap sebelum keputusan', 'Allow time for analysis', 'Respect their process'],
    motivation: ['Quality recognition', 'Learning opportunities', 'Expertise acknowledgment'],
    delegation: ['Clear specifications', 'Sufficient resources', 'Realistic timeline'],
    conflict: ['Use facts & data', 'Avoid emotional arguments', 'Give time to respond'],
    welfare: ['Quiet workspace', 'Clear expectations', 'Structured environment'],
    warning: ['Jangan rush tanpa info', 'Elak vague requirements', 'Jangan dismiss their concerns'],
    strengths: ['Detail-oriented', 'Accurate', 'Thorough', 'Quality-focused'],
    development: ['Speed/efficiency', 'Big picture thinking', 'Flexibility']
  },
  'Peacock': {
    leadership: ['Give spotlight opportunities', 'Involve in creative projects', 'Public recognition'],
    motivation: ['Excitement & variety', 'Social interaction', 'Creative freedom'],
    delegation: ['New & interesting projects', 'People-facing tasks', 'Brainstorming sessions'],
    conflict: ['Address quickly', 'Keep it light', 'Focus on relationship'],
    welfare: ['Social activities', 'Variety in work', 'Recognition programs'],
    warning: ['Jangan bagi routine boring', 'Elak isolation', 'Jangan ignore their ideas'],
    strengths: ['Creative', 'Enthusiastic', 'Great networker', 'Motivating'],
    development: ['Follow-through', 'Attention to detail', 'Time management']
  },
  'Eagle': {
    leadership: ['Give autonomy & authority', 'Set challenging targets', 'Direct communication'],
    motivation: ['Results & achievements', 'Leadership opportunities', 'Competitive challenges'],
    delegation: ['Clear objectives, flexible methods', 'Decision-making power', 'High-impact projects'],
    conflict: ['Direct & honest', 'Focus on results', 'Quick resolution'],
    welfare: ['Growth opportunities', 'Challenging work', 'Recognition of achievements'],
    warning: ['Jangan micromanage', 'Elak slow processes', 'Jangan challenge their authority publicly'],
    strengths: ['Decisive', 'Results-driven', 'Natural leader', 'Efficient'],
    development: ['Patience', 'Team collaboration', 'Empathy']
  }
};

export const COLOUR_BRAIN_HANDLING_GUIDE = {
  'Red': {
    communication: ['Terus kepada point, skip small talk', 'Fokus pada results dan outcomes', 'Bagi options, bukan arahan'],
    instruction: ['Set clear targets dengan deadline', 'Bagi autonomy untuk execute', 'Challenge mereka dengan goals'],
    motivation: ['Competition dan winning', 'Recognition untuk achievements', 'New challenges dan responsibilities'],
    warning: ['Jangan micromanage', 'Elak terlalu banyak details', 'Jangan buang masa mereka'],
    bestTasks: ['Closing deals', 'Crisis management', 'Quick decisions', 'Leading projects'],
    conflict: 'Direct confrontation - address issues head-on',
    stress: 'Give them action to take, not time to wait'
  },
  'Blue': {
    communication: ['Provide data dan evidence', 'Be specific dan detailed', 'Give time to process information'],
    instruction: ['Provide complete specs upfront', 'Allow questions untuk clarification', 'Set quality standards clearly'],
    motivation: ['Expertise recognition', 'Learning opportunities', 'Accurate dan quality work'],
    warning: ['Jangan rush tanpa data', 'Elak vague requirements', 'Jangan dismiss their concerns'],
    bestTasks: ['Data analysis', 'Quality control', 'Process documentation', 'Financial planning'],
    conflict: 'Use facts and logic - avoid emotional arguments',
    stress: 'Give them information and time to analyze'
  },
  'Green': {
    communication: ['Discuss ideas dan possibilities', 'Big picture before details', 'Allow brainstorming'],
    instruction: ['Explain the WHY and vision', 'Give creative freedom', 'Avoid rigid procedures'],
    motivation: ['New ideas dan innovation', 'Freedom to experiment', 'Recognition untuk creativity'],
    warning: ['Jangan terlalu rigid', 'Elak routine yang boring', 'Jangan limit their creativity'],
    bestTasks: ['Strategy planning', 'Innovation projects', 'Problem solving', 'Brand development'],
    conflict: 'Focus on future possibilities, not past problems',
    stress: 'Let them brainstorm solutions creatively'
  },
  'Yellow': {
    communication: ['Build relationship first', 'Show appreciation', 'Be warm dan supportive'],
    instruction: ['Explain impact on people/team', 'Involve them dalam decisions', 'Provide team support'],
    motivation: ['Team harmony', 'Helping others succeed', 'Positive relationships'],
    warning: ['Jangan be too aggressive', 'Elak public criticism', 'Jangan ignore their feelings'],
    bestTasks: ['Team coordination', 'Customer relations', 'Conflict mediation', 'Training delivery'],
    conflict: 'Address privately, focus on maintaining relationship',
    stress: 'Provide reassurance and team support'
  }
};

// ===== TASK RECOMMENDATIONS =====
export const TASK_RECOMMENDATIONS = {
  'urgent_deadline': { best: ['Eagle', 'Peacock'], avoid: ['Owl', 'Dove'], reason: 'Need speed & decisiveness' },
  'detailed_analysis': { best: ['Owl', 'Digital'], avoid: ['Peacock'], reason: 'Need accuracy & patience' },
  'client_facing': { best: ['Peacock', 'Audio'], avoid: ['Owl'], reason: 'Need people skills' },
  'team_coordination': { best: ['Dove', 'Audio'], avoid: ['Eagle'], reason: 'Need harmony & communication' },
  'creative_project': { best: ['Peacock', 'Visual'], avoid: ['Owl'], reason: 'Need creativity & ideas' },
  'process_improvement': { best: ['Owl', 'Digital'], avoid: ['Peacock'], reason: 'Need systematic thinking' },
  'crisis_management': { best: ['Eagle', 'Kinestetik'], avoid: ['Dove'], reason: 'Need quick action' },
  'training_delivery': { best: ['Peacock', 'Audio', 'Kinestetik'], avoid: ['Digital'], reason: 'Need engagement' },
  'documentation': { best: ['Owl', 'Digital', 'Visual'], avoid: ['Kinestetik'], reason: 'Need structure' },
  'negotiation': { best: ['Eagle', 'Peacock'], avoid: ['Dove'], reason: 'Need assertiveness' }
};

// ===== TEAM COMPATIBILITY =====
export const TEAM_COMPATIBILITY = {
  'Eagle-Dove': { score: 85, note: 'Eagle lead, Dove support - combo bagus!' },
  'Eagle-Owl': { score: 70, note: 'Eagle push speed, Owl ensure quality - balance' },
  'Eagle-Peacock': { score: 60, note: 'Both want spotlight - need clear roles' },
  'Eagle-Eagle': { score: 40, note: 'Power struggle risk - separate domains needed' },
  'Dove-Owl': { score: 90, note: 'Stable & accurate - great backend team' },
  'Dove-Peacock': { score: 75, note: 'Peacock energize, Dove stabilize' },
  'Dove-Dove': { score: 80, note: 'Harmonious but need external push' },
  'Owl-Peacock': { score: 55, note: 'Speed vs accuracy tension - need mediator' },
  'Owl-Owl': { score: 85, note: 'Quality team but may over-analyze' },
  'Peacock-Peacock': { score: 65, note: 'High energy but need structure' }
};
